import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

/* Word-by-word kinetic reveal — gentle entrance kept even under reduced-motion */
export function KineticText({ text, as: Tag = "span", className = "", delay = 0, stagger = 0.06, once = true, testId }) {
  const words = text.split(" ");
  const MotionTag = motion[Tag] || motion.span;
  return (
    <MotionTag
      className={className}
      data-testid={testId}
      initial="hidden"
      whileInView="show"
      viewport={{ once, amount: 0.4 }}
      variants={{ show: { transition: { staggerChildren: stagger, delayChildren: delay } } }}
      aria-label={text}
    >
      {words.map((w, i) => (
        <span key={`${i}-${w}`} className="inline-block overflow-hidden pb-[0.08em] align-baseline">
          <motion.span
            className="inline-block will-change-transform"
            variants={{
              hidden: { y: "110%", opacity: 0 },
              show: { y: 0, opacity: 1, transition: { duration: 0.8, ease: EASE } },
            }}
          >
            {w}
          </motion.span>
          {i < words.length - 1 ? "\u00A0" : ""}
        </span>
      ))}
    </MotionTag>
  );
}

/* Fade-rise on scroll — gentle motion kept even under reduced-motion */
export function Reveal({ children, className = "", delay = 0, y = 28, once = true, ...rest }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.25 }}
      transition={{ duration: 0.75, ease: EASE, delay }}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

/* 3D tilt card with cursor sheen */
export function TiltCard({ children, className = "", max = 8, ...rest }) {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(py, [0, 1], [max, -max]), { stiffness: 250, damping: 20 });
  const rotateY = useSpring(useTransform(px, [0, 1], [-max, max]), { stiffness: 250, damping: 20 });
  const sheen = useTransform([px, py], ([x, y]) =>
    `radial-gradient(circle at ${x * 100}% ${y * 100}%, hsl(var(--gold) / 0.16), transparent 60%)`
  );

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width);
    py.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => { px.set(0.5); py.set(0.5); };

  if (reduced) return <div className={className} {...rest}>{children}</div>;
  return (
    <motion.div
      ref={ref}
      onPointerMove={onMove}
      onPointerLeave={onLeave}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d", perspective: 1000 }}
      whileHover={{ scale: 1.015 }}
      className={`relative will-change-transform ${className}`}
      {...rest}
    >
      {children}
      <motion.div className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{ background: sheen }} />
    </motion.div>
  );
}

/* Magnetic wrapper for buttons/links */
export function Magnetic({ children, className = "", strength = 0.25 }) {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const x = useSpring(useMotionValue(0), { stiffness: 180, damping: 12, mass: 0.5 });
  const y = useSpring(useMotionValue(0), { stiffness: 180, damping: 12, mass: 0.5 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  };
  const reset = () => { x.set(0); y.set(0); };
  if (reduced) return <div className={className}>{children}</div>;
  return (
    <motion.div ref={ref} onPointerMove={onMove} onPointerLeave={reset} style={{ x, y }} className={`inline-block ${className}`}>
      {children}
    </motion.div>
  );
}
